/**
 * The plains hub as the approved 256 × 256 m map (lane C0-P1; layout data/layouts/plains.ts, plan
 * docs/design/C0_TOWN_LAYOUT.md). Laid as tiles the 2.5D client already draws: open ground, trees by species, rocks
 * and cliffs, water with plank bridges, paving, paddies, low walls, and the buildings as placeholders of the nearest
 * kit kind (step 3 of the build order draws them as themselves). Roads are a surface layer (`Surface.Earth`, the
 * market street `Surface.Stone`), so `onRoad` and the ground painters follow the layout's roads.
 *
 * Pure and deterministic: the map's own `mulberry32(seed)` and `hash2`, no time, no DOM. Values of map.ts are used
 * only inside functions (map.ts imports this file).
 */
import { plainsExitWarps } from './plainsExits';
import { clamp, hash2, mulberry32, valueNoise } from './rng';
import { zoneEnabled } from './data/enabledZones';
import { linksOf, ZONES, type Side, type ZoneId } from './data/zones';
import {
  fillUnreachable,
  isBorder,
  shapeTrainingSpots,
  Surface,
  Tile,
  type BuildingType,
  type GameMap,
  type Point,
  type Region,
  type Structure,
  type StructureKind,
} from './map';
import {
  AREAS,
  BRIDGE,
  BUILDINGS,
  CHESTS,
  ELDER,
  GATES,
  GUARD_POSTS,
  HORSE_STATIONS,
  HUB_WALL,
  LIFE_STATIONS,
  PLAINS_SIZE,
  PLAINS_PROPS,
  STEPPING_STONES,
  PLAZA,
  POCKETS,
  RING,
  ROADS,
  STELE,
  STREAM,
  STREAM_WIDTH,
  WAKE,
  type Box,
  type CRect,
  type PlainsBuilding,
  type XY,
} from './data/layouts/plains';

/**
 * Tree species in a `Tile.Tree`'s variant (0: the old pick by hash, as the 72 m map has): the client's tree sets
 * (render/scenery.ts `treeSprites`).
 */
export const TREE_SPECIES = ['', 'pine', 'bamboo', 'cypress', 'plum', 'willow'] as const;
const PINE = 1;
const BAMBOO = 2;
const CYPRESS = 3;
const PLUM = 4;

/** Tiles of a centre-and-size rectangle: [i0, i1, j0, j1). */
export const tilesOf = ([cx, cy, w, h]: CRect): Region => {
  const i0 = Math.round(cx - w / 2);
  const j0 = Math.round(cy - h / 2);
  return [i0, i0 + Math.max(1, Math.round(w)), j0, j0 + Math.max(1, Math.round(h))];
};

/** Distance from a point to a segment. */
function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const l = dx * dx + dy * dy;
  const t = l ? clamp(((px - ax) * dx + (py - ay) * dy) / l, 0, 1) : 0;
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
}

/** Whether a point is inside a polygon (even-odd). */
export function inPolygon(x: number, y: number, poly: readonly XY[]): boolean {
  let inside = false;
  for (let a = 0, b = poly.length - 1; a < poly.length; b = a++) {
    const [xa, ya] = poly[a]!;
    const [xb, yb] = poly[b]!;
    if (ya > y !== yb > y && x < ((xb - xa) * (y - ya)) / (yb - ya) + xa) inside = !inside;
  }
  return inside;
}

/** Calls `f` on each tile whose middle lies within `w / 2` of the polyline (at least a tile wide). */
function alongPath(pts: readonly XY[], w: number, size: number, f: (i: number, j: number, n: number) => void): void {
  const half = Math.max(w / 2, 0.75);
  const seen = new Set<number>();
  for (let k = 0; k + 1 < pts.length; k++) {
    const [ax, ay] = pts[k]!;
    const [bx, by] = pts[k + 1]!;
    const i0 = Math.max(0, Math.floor(Math.min(ax, bx) - half));
    const i1 = Math.min(size - 1, Math.ceil(Math.max(ax, bx) + half));
    const j0 = Math.max(0, Math.floor(Math.min(ay, by) - half));
    const j1 = Math.min(size - 1, Math.ceil(Math.max(ay, by) + half));
    for (let j = j0; j <= j1; j++)
      for (let i = i0; i <= i1; i++) {
        const n = j * size + i;
        if (seen.has(n) || segDist(i + 0.5, j + 0.5, ax, ay, bx, by) >= half) continue;
        seen.add(n);
        f(i, j, n);
      }
  }
}

/** What each layout building is drawn as, until it has art of its own. */
function kitOf(b: PlainsBuilding): { kind: StructureKind; type: BuildingType } | null {
  switch (b.kind) {
    case 'hall':
    case 'temple':
      return { kind: 'house', type: 'hall' };
    case 'trainhall':
    case 'jail':
      return { kind: 'house', type: 'guard' };
    case 'paifang':
      return { kind: 'gate', type: 'paifang' };
    case 'gate':
      return { kind: 'gate', type: 'gate' };
    case 'inn':
      return { kind: 'inn', type: 'inn' };
    case 'stage':
      return { kind: 'pavilion', type: 'shed' };
    case 'pagoda':
      return { kind: 'pagoda', type: 'pagoda' };
    case 'tower':
      return { kind: 'pagoda', type: 'tower' };
    case 'shop':
    case 'warehouse':
    case 'house':
      return { kind: 'house', type: 'house' };
    case 'stable':
      return { kind: 'house', type: 'stable' };
    case 'stall':
      return { kind: 'pavilion', type: 'stall' };
    case 'shrine':
      return { kind: 'pavilion', type: 'kiosk' };
    case 'pavilion':
      return { kind: 'pavilion', type: 'pavilion' };
    // A stockade and a camp are fences with tents (laid below), not one building.
    case 'stockade':
    case 'camp':
      return null;
  }
}

/** A solid prop's tile laid on paving: its variant is this plus the paving's style (the ground painter reads it). */
export const PAVED_UNDER = 128;
/** The hub wall's tiles (`Tile.Wall`): the client draws them as the town's low earth wall, not as stone blocks. */
export const HUB_WALL_VARIANT = 3;

/** Roof colour to the kit's look variant (house: 2 brick and red, 1 boards, 0 plain). */
const ROOF_VARIANT = { red: 2, grey: 0, green: 1, wood: 1 } as const;

/** The 256 m plains map (see the file's head). */
export function generatePlains256(seed: number): GameMap {
  const N = PLAINS_SIZE;
  const zone: ZoneId = 'plains';
  const rnd = mulberry32(seed * 131 + 7);
  const tiles = new Uint8Array(N * N);
  const variant = new Uint8Array(N * N);
  const owner = new Uint16Array(N * N);
  const surface = new Uint8Array(N * N);
  const structures: Structure[] = [];
  const at = ([x, y]: XY): Point => ({ x: x + 0.5, y: y + 0.5 });
  const box = (b: Box): Region => [b[0], b[1], b[2], b[3]];
  const pocket = (id: string): Region => box(POCKETS.find((p) => p.id === id)!.box);
  const cx = N / 2;
  const cy = N / 2;

  // Doors of the homes and the hub's gathering places (data/folk.ts names them as the 72 m map does).
  const front = (b: PlainsBuilding | undefined): Point => {
    if (!b) return at(STELE);
    const [i0, i1, , j1] = tilesOf(b.rect);
    return { x: (i0 + i1) / 2, y: j1 + 0.6 };
  };
  const named = (name: string, k = 0) => BUILDINGS.filter((b) => b.name === name)[k];
  const houses = BUILDINGS.filter((b) => b.kind === 'house');

  const links = linksOf(zone).filter((l) => zoneEnabled(l.to));
  const map: GameMap = {
    w: N,
    h: N,
    seed,
    tiles,
    variant,
    plaza: at(STELE),
    // The blood ledge (ผาแดง · ลานเลือด), the boss's ground.
    lair: at([230, 232]),
    // On the plaza's south rim, inside the safe circle: every save (old 72 m ones too) lands here.
    spawn: { x: STELE[0] + 0.5, y: STELE[1] + 11.5 },
    elder: at(ELDER),
    jail: at([54, 78]),
    // The world boss's ground: the ape field north of the hub.
    arena: at([128, 62]),
    portals: [...links.map(({ side, to }: { side: Side; to: ZoneId }) => ({
      side,
      to,
      ...(side === 'n'
        ? { x: cx + 0.5, y: 10.5 }
        : side === 's'
          ? { x: cx + 0.5, y: N - 3.5 }
          : side === 'w'
            ? { x: 3.5, y: cy + 0.5 }
            : { x: N - 10.5, y: cy + 0.5 }),
    })), ...plainsExitWarps()],
    duel: at(RING.at),
    regions: {
      wolves: pocket('K1'),
      bandits: pocket('K4'),
      west: pocket('K7'),
      east: pocket('K8'),
      graves: pocket('K9'),
    },
    structures,
    owner,
    biome: ZONES[zone].terrain,
    zone,
    kind: 'hub',
    surface,
    landmarks: {
      house1: front(houses[0]),
      house5: front(houses[1]),
      house13: front(houses[4]),
      house20: front(houses[5]),
      pavilion: front(BUILDINGS.find((b) => b.kind === 'pavilion')),
      pagoda: front(named('เจดีย์')),
      wake: at(WAKE),
      warehouse: front(named('โรงคลัง')),
      trainhall: front(named('โรงฝึกยุทธ์')),
      inn: front(named('โรงเตี๊ยม')),
      ...Object.fromEntries(HORSE_STATIONS.map((s) => [s.id, at(s.at)])),
      ...Object.fromEntries(GUARD_POSTS.map((g) => [g.id, at(g.at)])),
    },
  };

  const set = (n: number, t: number, v = 0) => {
    tiles[n] = t;
    variant[n] = v;
  };
  const inMap = (i: number, j: number) => i >= 0 && j >= 0 && i < N && j < N;
  const each = ([i0, i1, j0, j1]: Region, f: (i: number, j: number, n: number) => void) => {
    for (let j = Math.max(0, j0); j < Math.min(N, j1); j++)
      for (let i = Math.max(0, i0); i < Math.min(N, i1); i++) f(i, j, j * N + i);
  };
  const disc = ([x, y]: XY, r: number, f: (i: number, j: number, n: number, d: number) => void) =>
    each([Math.floor(x - r), Math.ceil(x + r) + 1, Math.floor(y - r), Math.ceil(y + r) + 1], (i, j, n) => {
      const d = Math.hypot(i + 0.5 - x, j + 0.5 - y);
      if (d < r) f(i, j, n, d);
    });
  /** How far a tile is inside a rectangle (metres to its nearest side). */
  const inset = ([i0, i1, j0, j1]: Region, i: number, j: number) => Math.min(i - i0, i1 - 1 - i, j - j0, j1 - 1 - j);
  const tree = (n: number, species: number) => set(n, Tile.Tree, species);
  const boulder = (n: number) => set(n, Tile.Rock, Math.floor(rnd() * 4));

  // ── Natural ground: groves and boulders outside the hub, then the grounds of the layout. ──
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const n = j * N + i;
      const x = i + 0.5;
      const y = j + 0.5;
      if (inPolygon(x, y, HUB_WALL)) continue;
      const grove = valueNoise(x / 7 + seed * 0.37, y / 7 + 11);
      if (grove > 0.74 && rnd() < 0.14) tree(n, hash2(i, j) < 0.6 ? PINE : CYPRESS);
      else if (rnd() < 0.004) tree(n, hash2(i * 3, j) < 0.5 ? PINE : PLUM);
      else if (Math.min(i, j, N - 1 - i, N - 1 - j) < 14 && valueNoise(x / 4 + 70, y / 4) > 0.8 && rnd() < 0.4)
        boulder(n);
    }
  for (const a of AREAS) {
    const r: Region = a.rect ? tilesOf(a.rect) : [0, 0, 0, 0];
    switch (a.kind) {
      case 'bamboo':
      case 'pine':
        each(r, (i, j, n) => {
          // Thinner toward the edge, so the wood's rim is ragged.
          const edge = clamp(inset(r, i, j) / 5, 0.25, 1);
          const p = (a.kind === 'bamboo' ? 0.1 : 0.075) * edge * (0.6 + valueNoise(i / 5 + 3, j / 5) * 0.8);
          if (rnd() < p) tree(n, a.kind === 'bamboo' ? BAMBOO : hash2(i, j * 3) < 0.85 ? PINE : CYPRESS);
          else if (tiles[n] === Tile.Tree) set(n, Tile.Floor);
        });
        break;
      case 'hill':
        each(r, (_i, _j, n) => {
          set(n, Tile.Floor);
          const h = rnd();
          if (h < 0.025) tree(n, PINE);
          else if (h < 0.045) boulder(n);
        });
        break;
      case 'garden':
        each(r, (_i, _j, n) => set(n, Tile.Floor));
        each(r, (_i, _j, n) => {
          if (rnd() < 0.05) tree(n, PLUM);
        });
        break;
      case 'grave':
        each(r, (i, j, n) => {
          set(n, Tile.Floor);
          // Rows of grave stones, cypresses between.
          if (i % 4 === 0 && j % 3 === 0 && rnd() < 0.7) set(n, Tile.Rock, 2);
          else if (rnd() < 0.025) tree(n, CYPRESS);
        });
        break;
      case 'marsh':
        each(r, (i, j, n) => {
          const wet = valueNoise(i / 4 + 9, j / 4 + 40) - inset(r, i, j) * -0.02;
          set(n, inset(r, i, j) > 1 && wet > 0.56 ? Tile.Water : Tile.Floor);
        });
        break;
      case 'pond':
        each(r, (i, j, n) => {
          const [ccx, ccy, w, h] = a.rect!;
          const e = ((i + 0.5 - ccx) / (w / 2)) ** 2 + ((j + 0.5 - ccy) / (h / 2)) ** 2;
          set(n, e < 0.8 ? Tile.Water : Tile.Floor);
        });
        break;
      case 'farm':
        each(r, (i, j, n) => {
          // Paddies in terraced strips, earth bunds between them.
          const bund = (j - r[2]) % 6 === 0 || (i - r[0]) % 12 === 0;
          set(n, bund ? Tile.Floor : Tile.Paddy);
        });
        break;
      case 'ruin':
        each(r, (i, j, n) => {
          const k = inset(r, i, j);
          if (k > 3 && k < 16) set(n, Tile.Paved, 1);
          else set(n, Tile.Floor);
          // A broken wall round the court, rubble and weeds inside.
          if (k === 3 && hash2(i * 7, j * 5) < 0.6) set(n, Tile.Wall, 1);
          else if (k > 3 && rnd() < 0.03) boulder(n);
          else if (k <= 2 && rnd() < 0.05) tree(n, CYPRESS);
        });
        break;
      case 'field':
        disc(a.c!, a.clear ?? a.r!, (_i, _j, n) => set(n, Tile.Floor));
        break;
      case 'ring':
        disc(a.c!, a.r!, (_i, _j, n, d) => {
          if (d < a.r! - 4) set(n, Tile.Paved, 2);
          else if (d < a.r! - 3 && hash2(n, 3) < 0.55) set(n, Tile.Rock, 4 + (n & 1));
          else set(n, Tile.Floor);
        });
        break;
      case 'ledge':
        disc(a.c!, a.r!, (i, j, n, d) => {
          const gate = i + 0.5 < a.c![0] && j + 0.5 < a.c![1] + 3 && j + 0.5 > a.c![1] - 3;
          set(n, d > a.r! - 1.4 && !gate ? Tile.Rock : Tile.Floor, d > a.r! - 1.4 ? 4 + (n & 1) : 0);
        });
        break;
      case 'clearing':
      case 'homesGround':
        each(r, (_i, _j, n) => set(n, Tile.Floor));
        break;
      case 'paved':
        each(r, (_i, _j, n) => set(n, Tile.Paved, 0));
        break;
      case 'terrace':
        each(r, (_i, _j, n) => set(n, Tile.Paved, 1));
        break;
      case 'street':
        each(r, (_i, _j, n) => {
          set(n, Tile.Floor);
          surface[n] = Surface.Stone;
        });
        break;
      case 'yardDirt':
        each(r, (_i, _j, n) => {
          set(n, Tile.Floor);
          surface[n] = Surface.Earth;
        });
        break;
      case 'cliff':
        break;
    }
  }

  // ── NPC-life stations that shape the ground: the buffalo's wallow pool, the town shrine's paved courtyard. ──
  for (const l of LIFE_STATIONS) {
    if (l.act === 'wallow') disc(l.at, 1.8, (_i, _j, n) => set(n, Tile.Water));
    else if (l.act === 'pray') disc(l.at, l.r, (_i, _j, n) => set(n, Tile.Paved, 1));
  }

  // ── Water: the stream, then roads over everything (plank bridges where they cross water). ──
  alongPath(STREAM, STREAM_WIDTH, N, (_i, _j, n) => {
    set(n, Tile.Water);
    surface[n] = Surface.Grass;
  });
  {
    const [bx, by] = BRIDGE.at;
    each(tilesOf([bx, by, BRIDGE.w, BRIDGE.h]), (_i, _j, n) => {
      if (tiles[n] === Tile.Water) set(n, Tile.Pier);
    });
  }
  for (const road of ROADS)
    alongPath(road.pts, road.w, N, (_i, _j, n) => {
      if (tiles[n] === Tile.Water || tiles[n] === Tile.Pier) set(n, Tile.Pier);
      else if (tiles[n] !== Tile.Paved) {
        set(n, Tile.Floor);
        if (surface[n] !== Surface.Stone) surface[n] = Surface.Earth;
      }
    });

  // ── The F2 stepping stones: a walkable stone tile each in the stream (collision = the tile; the prop draws the stone). ──
  for (const [sx, sy] of STEPPING_STONES) {
    const n = Math.floor(sy) * N + Math.floor(sx);
    set(n, Tile.Floor);
    surface[n] = Surface.Stone;
  }

  // ── The hub's low wall, open where roads, trails and the stream pass (its gates). ──
  const open = (i: number, j: number) => {
    for (let b = j - 1; b <= j + 1; b++)
      for (let a = i - 1; a <= i + 1; a++) {
        if (!inMap(a, b)) continue;
        const n = b * N + a;
        if (surface[n] !== Surface.Grass || tiles[n] === Tile.Water || tiles[n] === Tile.Pier) return true;
      }
    return GATES.some((g) => Math.hypot(i + 0.5 - g.at[0], j + 0.5 - g.at[1]) < 2.5);
  };
  for (let k = 0; k < HUB_WALL.length; k++) {
    const [ax, ay] = HUB_WALL[k]!;
    const [bx, by] = HUB_WALL[(k + 1) % HUB_WALL.length]!;
    const steps = Math.ceil(Math.hypot(bx - ax, by - ay) * 3);
    for (let s = 0; s <= steps; s++) {
      const i = Math.floor(ax + ((bx - ax) * s) / steps);
      const j = Math.floor(ay + ((by - ay) * s) / steps);
      if (!inMap(i, j) || open(i, j)) continue;
      set(j * N + i, Tile.Wall, HUB_WALL_VARIANT);
    }
  }

  /** A layout point (a tile) as the metres of its middle. */
  const mid = ([x, y]: XY): XY => [x + 0.5, y + 0.5];
  // ── The plaza (paved R 12, its curb clear to R 13) and the stele; the duel ring. ──
  disc(mid(PLAZA.at), PLAZA.r + 1, (_i, _j, n, d) => {
    set(n, d < PLAZA.r ? Tile.Paved : Tile.Floor, 0);
    surface[n] = Surface.Grass;
  });
  set(STELE[1] * N + STELE[0], Tile.Stele);
  disc(mid(RING.at), RING.r, (i, j, n, d) => {
    // Stands of stone on the ring's north-west half only; the front half is open (C0_SCENE_PLAINS §5).
    const back = i - RING.at[0] + (j - RING.at[1]) < -1;
    if (d < RING.floor) set(n, Tile.Paved, 2);
    else if (back && d < RING.floor + 1.2) set(n, Tile.Rock, 4 + (n & 1));
    else if (tiles[n] !== Tile.Paved) set(n, Tile.Floor);
  });

  // ── Buildings: solid footprints (gates: their pillars), drawn as the nearest kit kind. ──
  const build = (st: Structure, solid: readonly (readonly [number, number])[]) => {
    structures.push(st);
    for (const [i, j] of solid) {
      if (!inMap(i, j)) continue;
      set(j * N + i, Tile.Building);
      owner[j * N + i] = structures.length;
    }
  };
  for (const b of BUILDINGS) {
    const [i0, i1, j0, j1] = tilesOf(b.rect);
    const kit = kitOf(b);
    if (!kit) {
      // A stockade or a camp: a fence with an opening on each side, tents inside.
      each([i0, i1, j0, j1], (i, j, n) => {
        const ring = i === i0 || i === i1 - 1 || j === j0 || j === j1 - 1;
        const gap = Math.abs(i + 0.5 - (i0 + i1) / 2) < 2 || Math.abs(j + 0.5 - (j0 + j1) / 2) < 2;
        set(n, ring && !gap ? Tile.Wall : Tile.Floor, ring ? 2 : 0);
      });
      const tents: XY[] = [
        [i0 + 3, j0 + 3],
        [i1 - 7, j0 + 3],
        [i0 + 3, j1 - 7],
      ];
      for (const [i, j] of tents) {
        const solid: [number, number][] = [];
        for (let dj = 0; dj < 4; dj++) for (let di = 0; di < 4; di++) solid.push([i + di, j + dj]);
        build({ kind: 'pavilion', type: 'tent', i, j, w: 4, h: 4, variant: 1, name: b.name }, solid);
      }
      continue;
    }
    const w = i1 - i0;
    const h = j1 - j0;
    const v = ROOF_VARIANT[b.roof ?? 'red'];
    if (kit.kind === 'gate') {
      // Pillars at both ends; the road runs under the beam.
      build({ kind: 'gate', type: kit.type, i: i0, j: j0, w, h: 1, axis: 'x', variant: 0, name: b.name }, [
        [i0, j0],
        [i1 - 1, j0],
      ]);
      continue;
    }
    const solid: [number, number][] = [];
    for (let j = j0; j < j1; j++) for (let i = i0; i < i1; i++) solid.push([i, j]);
    // H10: the warehouse and training hall can be entered (data/interiors.ts DOOR_LINKS), door on the south face.
    const enter = b.kind === 'warehouse' || b.kind === 'trainhall' ? `plains-${b.kind}` : undefined;
    build(
      {
        kind: kit.kind,
        type: kit.type,
        i: i0,
        j: j0,
        w,
        h,
        variant: v,
        name: b.name,
        ...(enter ? { id: enter, door: { i: i0 + (w >> 1), j: j1 - 1, facing: 's' as const } } : {}),
      },
      solid,
    );
  }

  // ── The kit's solid props (wells, carts, racks, banners, gate posts, lanterns…) block their tile. On paving the
  // tile keeps its paving style in the variant (+ PAVED_UNDER), so the ground painter still lays the flags under it.
  for (const pr of PLAINS_PROPS) {
    if (!pr.solid) continue;
    const i = Math.floor(pr.at[0]);
    const j = Math.floor(pr.at[1]);
    if (!inMap(i, j)) continue;
    const n = j * N + i;
    const t = tiles[n];
    if (t !== Tile.Floor && t !== Tile.Paved && t !== Tile.Paddy && t !== Tile.Tree && t !== Tile.Rock) continue;
    set(n, Tile.Building, t === Tile.Paved ? PAVED_UNDER + variant[n]! : 0);
  }

  // ── The edges: the north cliff wall and the east cliffs (tall, forested), the border rock all round. ──
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const n = j * N + i;
      const north = j < 8 ? j : 99;
      const east = i >= N - 8 ? N - 1 - i : 99;
      const d = Math.min(north, east);
      if (d < 99) {
        if (d < 2) set(n, Tile.Rock, 8 + (hash2(i, j) < 0.5 ? 0 : 1));
        else if (d < 5) set(n, Tile.Rock, 6 + (hash2(i * 3, j) < 0.5 ? 0 : 1));
        else if (d < 7 || hash2(i, j * 7) < 0.5) set(n, Tile.Rock, 4 + (hash2(i, j * 5) < 0.5 ? 0 : 1));
      } else if (isBorder(map, i, j)) set(n, Tile.Rock, 4 + (hash2(i * 5, j) < 0.5 ? 0 : 1));
    }

  // ── Spots kept open: the spawn, the elder, the wake spot, guard posts, stations, chests, the jail cell. ──
  const keep = (p: XY | Point, r: number) => {
    const [x, y] = 'x' in p ? [p.x, p.y] : [p[0] + 0.5, p[1] + 0.5];
    disc([x, y], r, (_i, _j, n) => {
      const t = tiles[n];
      if (t === Tile.Tree || t === Tile.Rock || t === Tile.Wall) set(n, Tile.Floor);
    });
  };
  keep(map.spawn, 2);
  keep(map.elder, 1.5);
  keep(WAKE, 2);
  keep(map.jail, 3);
  keep(map.lair, 4);
  keep(map.arena, 7);
  for (const g of GUARD_POSTS) keep(g.at, 1.5);
  for (const s of HORSE_STATIONS) keep(s.at, 3);
  for (const c of CHESTS) keep(c, 1.5);
  for (const p of map.portals) keep(p, 2.6);

  shapeTrainingSpots(map);
  fillUnreachable(map, Math.floor(map.spawn.x), Math.floor(map.spawn.y), rnd);
  return map;
}
